import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Leaf, Mountain, Package } from "lucide-react";
import { useStore } from "../context/StoreContext";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.13 },
  }),
};

const MaskedLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.18em] -mb-[0.18em]">
    <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, img } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  const badges = [
    { icon: Leaf, label: t.hero.badge1 },
    { icon: Mountain, label: t.hero.badge2 },
    { icon: Package, label: t.hero.badge3 },
  ];

  return (
    <section ref={ref} id="top" className="relative min-h-screen grid lg:grid-cols-12 pt-24 lg:pt-0 overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: textY }} className="lg:col-span-7 flex flex-col justify-center px-6 lg:pl-14 xl:pl-24 lg:pr-8 pt-2 pb-14 lg:pt-24 lg:pb-20 relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#C85A32]" />
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif text-[2rem] sm:text-4xl lg:text-5xl xl:text-[3.5rem] leading-[1.12] tracking-tight text-stone-900" data-testid="hero-headline">
          <MaskedLine i={0} className="tracking-[0.14em]">NŪRA</MaskedLine>
          <MaskedLine i={1}>{t.hero.line1}</MaskedLine>
          <MaskedLine i={2} className="italic text-[#C85A32]">{t.hero.line2}</MaskedLine>
          <MaskedLine i={3}>{t.hero.line3}</MaskedLine>
          <MaskedLine i={4} className="italic text-[#4A624E]">{t.hero.line4}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 max-w-md text-base text-stone-600 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#products"
            data-testid="hero-cta-collection"
            className="group inline-flex items-center gap-3 rounded-full bg-stone-900 text-[#FAF7F2] px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] hover:bg-[#C85A32] transition-colors duration-300"
          >
            {t.hero.cta1}
            <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </a>
          <a href="#manifesto" data-testid="hero-cta-manifesto" className="text-[12px] uppercase tracking-[0.18em] text-stone-700 border-b border-stone-400 pb-1 hover:text-[#4A624E] hover:border-[#4A624E] transition-colors duration-300">
            {t.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap gap-6"
        >
          {badges.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500">
              <Icon size={13} className="text-[#4A624E]" />
              {label}
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="lg:col-span-5 relative min-h-[50vh] lg:min-h-screen">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          className="absolute inset-0 lg:inset-y-0 lg:right-0 overflow-hidden"
        >
          <motion.img
            style={{ y: imgY, scale: imgScale }}
            src={img("heroLifestyle")}
            alt="Rituel NŪRA — boisson adaptogène"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/30 via-transparent to-transparent" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6 }}
          className="absolute bottom-8 left-8 right-8 lg:left-10 lg:right-auto backdrop-blur-md bg-[#FAF7F2]/80 border border-white/50 rounded-2xl px-6 py-4 max-w-xs"
        >
          <p className="font-serif text-lg text-stone-900 italic">{t.hero.quote}</p>
          <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.25em] text-stone-500">{t.hero.quoteSub}</p>
        </motion.div>
      </div>
    </section>
  );
};
